/**
 * How the editor area is split into groups: a leaf, or a row or a column of
 * grids, each with its share of the branch's size. Leaves are objects that
 * have no `direction` field. The functions return new branches along the path
 * they change and keep the others.
 */
export type Grid<T> = T | Branch<T>;

export interface Branch<T> {
    direction: "row" | "column";
    children: Grid<T>[];
    /** Each child's share of the branch's size; they add up to 1. */
    sizes: number[];
}

/** Where a new group goes beside an existing one. */
export type Side = "left" | "right" | "up" | "down";

/** The smallest a group can be made by dragging a sash, in pixels. */
const MIN_SIZE = 100;

export function isBranch<T>(grid: Grid<T>): grid is Branch<T> {
    return typeof grid === "object" && grid !== null && "direction" in grid;
}

/** The leaves in reading order: left to right, top to bottom. */
export function leaves<T>(grid: Grid<T>): T[] {
    return isBranch(grid) ? grid.children.flatMap((child) => leaves(child)) : [grid];
}

export function mapGrid<T, S>(grid: Grid<T>, map: (leaf: T) => S): Grid<S> {
    if (!isBranch(grid)) return map(grid);
    return {
        direction: grid.direction,
        children: grid.children.map((child) => mapGrid(child, map)),
        sizes: [...grid.sizes],
    };
}

/**
 * Put `added` on one side of `target`, in half of its space. A branch already
 * in that direction takes `added` as a sibling; otherwise `target` becomes a
 * branch of the two.
 */
export function split<T>(grid: Grid<T>, target: T, added: T, side: Side): Grid<T> {
    const direction = side === "left" || side === "right" ? "row" : "column";
    const after = side === "right" || side === "down";
    const visit = (node: Grid<T>): Grid<T> => {
        if (!isBranch(node)) {
            if (node !== target) return node;
            return { direction, children: after ? [node, added] : [added, node], sizes: [0.5, 0.5] };
        }
        const at = node.children.indexOf(target);
        if (at < 0 || node.direction !== direction) return { ...node, children: node.children.map(visit) };
        const children = [...node.children];
        const sizes = [...node.sizes];
        const half = sizes[at]! / 2;
        sizes[at] = half;
        children.splice(after ? at + 1 : at, 0, added);
        sizes.splice(after ? at + 1 : at, 0, half);
        return { direction, children, sizes };
    };
    return visit(grid);
}

/** The leaf next to `leaf` on one side, in a branch of that direction, if there is one. */
export function beside<T>(grid: Grid<T>, leaf: T, side: Side): T | undefined {
    if (!isBranch(grid)) return undefined;
    const at = grid.children.indexOf(leaf);
    if (at < 0) {
        for (const child of grid.children) {
            const found = beside(child, leaf, side);
            if (found !== undefined) return found;
        }
        return undefined;
    }
    if (grid.direction !== (side === "left" || side === "right" ? "row" : "column")) return undefined;
    const next = grid.children[side === "right" || side === "down" ? at + 1 : at - 1];
    return next === undefined || isBranch(next) ? undefined : next;
}

/**
 * Take a leaf out and give its space to its siblings among `heirs`, in
 * proportion to their sizes, or else to the neighbour before it, or after it
 * if it is first. A branch left with one child is replaced by the child, and
 * a branch inside one of the same direction is merged into it.
 */
export function remove<T>(grid: Grid<T>, leaf: T, heirs: readonly T[] = []): Grid<T> {
    const visit = (node: Grid<T>): Grid<T> => {
        if (!isBranch(node)) return node;
        let children = [...node.children];
        let sizes = [...node.sizes];
        const at = children.indexOf(leaf);
        if (at >= 0) {
            const near = children.flatMap((child, i) => (heirs.includes(child as T) ? [i] : []));
            if (near.length === 0) near.push(at > 0 ? at - 1 : 1);
            const total = near.reduce((sum, i) => sum + sizes[i]!, 0);
            for (const i of near) sizes[i]! += (sizes[at]! * sizes[i]!) / total;
            children.splice(at, 1);
            sizes.splice(at, 1);
        } else {
            children = children.map(visit);
        }
        if (children.length === 1) return children[0]!;
        const flat: Branch<T> = { direction: node.direction, children: [], sizes: [] };
        children.forEach((child, i) => {
            if (isBranch(child) && child.direction === node.direction) {
                flat.children.push(...child.children);
                flat.sizes.push(...child.sizes.map((size) => size * sizes[i]!));
            } else {
                flat.children.push(child);
                flat.sizes.push(sizes[i]!);
            }
        });
        return flat;
    };
    return visit(grid);
}

/** Whether `value` is a grid whose leaves pass `isLeaf`, as a stored layout must be before it is used. */
export function isGrid<T>(value: unknown, isLeaf: (leaf: unknown) => leaf is T): value is Grid<T> {
    if (typeof value !== "object" || value === null || !("direction" in value)) return isLeaf(value);
    const { direction, children, sizes } = value as Partial<Branch<unknown>>;
    return (
        (direction === "row" || direction === "column") &&
        Array.isArray(children) &&
        Array.isArray(sizes) &&
        children.length >= 2 &&
        sizes.length === children.length &&
        sizes.every((size) => typeof size === "number" && size > 0) &&
        children.every((child) => isGrid(child, isLeaf))
    );
}

/**
 * Lay out the grid in nested flex boxes, with a sash between neighbours.
 * Dragging a sash changes the shares in the branch's `sizes` and calls
 * `resized` when the drag ends.
 */
export function renderGrid<T>(grid: Grid<T>, element: (leaf: T) => HTMLElement, resized: () => void): HTMLElement {
    if (!isBranch(grid)) return element(grid);
    const box = document.createElement("div");
    box.className = `wb-split wb-split-${grid.direction}`;
    const items = grid.children.map((child) => renderGrid(child, element, resized));
    items.forEach((item, i) => {
        item.style.flex = `${grid.sizes[i]} 1 0px`;
        if (i > 0) box.append(sash(grid, i - 1, items, resized));
        box.append(item);
    });
    return box;
}

/** The sash between the branch's children `i` and `i + 1`. */
function sash<T>(branch: Branch<T>, i: number, items: HTMLElement[], resized: () => void): HTMLElement {
    const row = branch.direction === "row";
    const element = document.createElement("div");
    element.className = `wb-sash ${row ? "wb-sash-vertical" : "wb-sash-horizontal"}`;
    const dragging = row ? "wb-dragging-x" : "wb-dragging-y";
    const extent = (item: HTMLElement) => (row ? item.offsetWidth : item.offsetHeight);
    let origin = 0;
    let before = 0;
    let total = 0;
    element.addEventListener("pointerdown", (event) => {
        element.setPointerCapture(event.pointerId);
        origin = row ? event.clientX : event.clientY;
        before = extent(items[i]!);
        total = before + extent(items[i + 1]!);
        document.body.classList.add(dragging);
        event.preventDefault();
    });
    element.addEventListener("pointermove", (event) => {
        if (!element.hasPointerCapture(event.pointerId) || total === 0) return;
        const moved = before + (row ? event.clientX : event.clientY) - origin;
        const size = Math.max(Math.min(MIN_SIZE, total / 2), Math.min(total - MIN_SIZE, moved));
        const shared = branch.sizes[i]! + branch.sizes[i + 1]!;
        branch.sizes[i] = (shared * size) / total;
        branch.sizes[i + 1] = shared - branch.sizes[i]!;
        items[i]!.style.flex = `${branch.sizes[i]} 1 0px`;
        items[i + 1]!.style.flex = `${branch.sizes[i + 1]} 1 0px`;
    });
    const stop = (event: PointerEvent) => {
        if (!element.hasPointerCapture(event.pointerId)) return;
        element.releasePointerCapture(event.pointerId);
        document.body.classList.remove(dragging);
        resized();
    };
    element.addEventListener("pointerup", stop);
    element.addEventListener("pointercancel", stop);
    return element;
}
